import React from 'react';
import Icon from '../../../components/AppIcon';

const PredictiveDashboardCard = ({ systemHealth, predictions, anomalies, loading }) => {
  const getRiskLevelColor = (riskLevel) => {
    const colors = {
      critical: 'text-red-600 bg-red-50',
      high: 'text-orange-600 bg-orange-50',
      medium: 'text-yellow-600 bg-yellow-50',
      low: 'text-green-600 bg-green-50'
    };
    return colors?.[riskLevel] || 'text-gray-600 bg-gray-50';
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const criticalPredictions = predictions?.filter(p => p?.severity === 'critical')?.length || 0;
  const highPredictions = predictions?.filter(p => p?.severity === 'high')?.length || 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Activity" className="w-5 h-5 text-purple-600" />
          Predictive Analytics Dashboard
        </h2>
      </div>
      <div className="p-6 space-y-6">
        {/* System Health Score */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-2">System Health Score</div>
          <div className={`text-4xl font-bold ${
            systemHealth?.healthScore
              ? getHealthScoreColor(systemHealth?.healthScore)
              : 'text-gray-400'
          }`}>
            {systemHealth?.healthScore ? `${systemHealth?.healthScore}%` : '--'}
          </div>
          {systemHealth?.riskLevel && (
            <div className="mt-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                getRiskLevelColor(systemHealth?.riskLevel)
              }`}>
                {systemHealth?.riskLevel?.toUpperCase()} RISK
              </span>
            </div>
          )}
        </div>

        {/* Predicted Failures */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-3">Predicted Failures (24-48h)</div>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-900">Critical</span>
              </div>
              <span className="text-lg font-bold text-red-600">{criticalPredictions}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="AlertTriangle" className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-900">High Priority</span>
              </div>
              <span className="text-lg font-bold text-orange-600">{highPredictions}</span>
            </div>
          </div>
        </div>

        {/* Anomaly Detection */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-3">Real-time Anomalies</div>
          <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="Zap" className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-900">Active Anomalies</span>
            </div>
            <span className="text-lg font-bold text-yellow-600">{anomalies?.length || 0}</span>
          </div>
        </div>

        {/* Automated Interventions */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-3">Intervention Success Rate</div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Automated Fixes</span>
              <span className="font-medium text-gray-900">94.2%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-green-600 h-2 rounded-full" style={{ width: '94.2%' }}></div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">Total Errors</div>
            <div className="text-xl font-bold text-gray-900">{systemHealth?.errorCount || 0}</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">API Health</div>
            <div className="text-xl font-bold text-green-600">
              {systemHealth?.apiHealth?.successRate || 0}%
            </div>
          </div>
        </div>

        {/* AI Analysis Status */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Icon name="Sparkles" className="w-4 h-4 text-purple-600" />
            <span>Powered by Anthropic Claude AI</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveDashboardCard;
